import type { Entity, PaginatedResponse, Sample, Test, Assay, Order, EntityType } from '@mariana/labos-client';
import type { ColumnDef } from '../config/viewsConfig';
import { limsClient } from '../api/limsClient';

type AnyEntity = Sample | Test | Assay | Order;

export type ResolvedEntityMap = Map<string, Map<number, AnyEntity>>;

export async function fetchEntitiesByIds(entityType: EntityType, ids: number[]): Promise<AnyEntity[]> {
  if (ids.length === 0) return [];

  const opts = {
    filters: [{ attribute_name: 'id' as const, operator: 'IN' as const, value: ids }],
    limit: ids.length,
  };
  const response = await limsClient.listEntitiesByType(entityType, opts);
  return response.data as AnyEntity[];
}

/**
 * Given entities and column definitions, batch-fetches any referenced entities
 * needed by columns with dot-notation keys (e.g. `sample_id.sample_name`).
 *
 * Returns a map keyed by FK attribute name, whose values are maps
 * from foreign ID to the resolved entity.
 */
export async function resolveSourceColumns(
  entities: Entity[],
  columns: ColumnDef[],
  entityType: EntityType,
): Promise<ResolvedEntityMap> {
  const resolved: ResolvedEntityMap = new Map();

  const crossEntityCols = columns.filter(col => col.key.includes('.'));
  if (crossEntityCols.length === 0) return resolved;

  const attrs = await limsClient.getAttributes(entityType);
  const fkAttrNames = Array.from(new Set(crossEntityCols.map(col => col.key.split('.', 2)[0])));
  const sourceGroups = new Map<string, { entityType: EntityType; ids: Set<number> }>();

  for (const fkAttrName of fkAttrNames) {
    const attrDef = attrs.find(a => a.name === fkAttrName && a.referenced_entity_type);
    if (!attrDef) continue;

    const ids = new Set<number>();
    for (const entity of entities) {
      const foreignId = entity.attributes?.[fkAttrName];
      if (typeof foreignId === 'number') {
        ids.add(foreignId);
      }
    }
    if (ids.size > 0) {
      sourceGroups.set(fkAttrName, { entityType: attrDef.referenced_entity_type!, ids });
    }
  }

  const fetches = Array.from(sourceGroups.entries()).map(async ([fkAttr, { entityType: refType, ids }]) => {
    const fetched = await fetchEntitiesByIds(refType, Array.from(ids));
    const map = new Map<number, AnyEntity>();
    for (const e of fetched) {
      if (e.id != null) map.set(e.id, e);
    }
    resolved.set(fkAttr, map);
  });

  await Promise.all(fetches);
  return resolved;
}

/**
 * Look up a resolved value for a cross-entity column (dot-notation key).
 */
export function getResolvedValue(
  entity: Entity,
  col: ColumnDef,
  resolvedEntities: ResolvedEntityMap,
): string | number | boolean | null | undefined {
  if (!col.key.includes('.')) return undefined;
  const [fkAttr, targetAttr] = col.key.split('.', 2);
  const foreignId = entity.attributes?.[fkAttr];
  if (typeof foreignId !== 'number') return undefined;
  const refEntity = resolvedEntities.get(fkAttr)?.get(foreignId);
  return refEntity?.attributes?.[targetAttr];
}
